import '../styles.css'

import { ContentTabs, Vertical } from '@gravitate-js/excalibrr'
import { Modal } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import { Footer } from './Footer'
import { ImageViewer } from '@/components/_SiteWide/ImageViewer'
import { uniqBy } from 'lodash'
import api from '@/utils/api'
import { useImageUpload } from '@/hooks/useImageUpload'
import { useQueryClient } from '@tanstack/react-query'
import { useOrderMovements } from '@/api/useOrderMovements'

type DropAttachmentModalProps = {
  visible: boolean
  setShowingModal: (show: boolean) => void
  movementDetails: Gravitate.OrderMovementDetails | undefined
  canEditOrderMovements: boolean
  onChangeImages: (updatedImages: Gravitate.DeliveryAttachment[], storeNumber: string) => void
}

export function DropAttachmentModal({
  visible,
  setShowingModal,
  movementDetails,
  canEditOrderMovements,
  onChangeImages,
}: DropAttachmentModalProps) {
  const drops = useMemo(() => {
    return movementDetails?.store_drops ?? []
  }, [movementDetails])
  const queryClient = useQueryClient()
  const { useDeleteDropAttachment, useUploadDropAttachment } = useOrderMovements()
  const { mutateAsync: uploadDropAttachment } = useUploadDropAttachment()
  const { mutateAsync: deleteDropAttachment } = useDeleteDropAttachment()

  const uniqueDrops = uniqBy(drops, 'store')
  const [selectedStoreNumber, setSelectedStoreNumber] = useState('')

  useEffect(() => {
    if (!selectedStoreNumber) setSelectedStoreNumber(uniqueDrops[0]?.store || '')
  }, [uniqueDrops, selectedStoreNumber])

  const currentImages = useMemo(() => {
    const attachmentObj = movementDetails?.delivery_attachments?.find((obj) => obj[selectedStoreNumber])
    return attachmentObj?.[selectedStoreNumber] || []
  }, [movementDetails?.delivery_attachments, selectedStoreNumber])

  async function handleDelete(image: Gravitate.DeliveryAttachment) {
    return api.deleteDropAttachment(movementDetails?.order_number, selectedStoreNumber, image.filename).then(() => {
      if (movementDetails?.order_id !== undefined) {
        queryClient.invalidateQueries({ queryKey: ['orderMovementDetailQuery', movementDetails.order_id] })
      }
    })
  }

  async function handleUpload(fd: FormData) {
    return api.uploadDropAttachment(movementDetails?.order_id, selectedStoreNumber, fd).then(() => {
      if (movementDetails?.order_id) {
        queryClient.invalidateQueries({ queryKey: ['orderMovementDetailQuery', movementDetails.order_id] })
      }
    })
  }

  function handleImagesChange(updatedImages: Gravitate.DeliveryAttachment[]) {
    onChangeImages(updatedImages, selectedStoreNumber)
  }

  const {
    images,
    selectedImageIndex,
    isUploading,
    setSelectedImageIndex,
    uploadImage,
    deleteImage,
    resetImageSelection,
  } = useImageUpload<Gravitate.DeliveryAttachment>({
    images: currentImages,
    uploadApi: handleUpload,
    deleteApi: handleDelete,
    onImagesChange: handleImagesChange,
  })

  function resetImageModal() {
    resetImageSelection()
    setSelectedStoreNumber('')
    setShowingModal(false)
  }

  function handleStoreChange(storeNumber: string) {
    resetImageSelection()
    setSelectedStoreNumber(storeNumber)
  }

  const tabItems = useMemo(() => {
    return uniqueDrops.map((drop) => {
      const storeAttachments = movementDetails?.delivery_attachments?.find((obj) => obj[drop.store])
      const storeCount = storeAttachments?.[drop.store]?.length || 0

      return {
        key: drop.store,
        title: drop.store,
        count: storeCount,
      }
    })
  }, [uniqueDrops, movementDetails?.delivery_attachments])

  return (
    <Modal
      classNames={{ body: 'image-modal' }}
      title={`#${movementDetails?.order_number} Site Attachments`}
      open={visible}
      width='80vw'
      onCancel={resetImageModal}
      onOk={resetImageModal}
      destroyOnClose
      footer={
        <Footer
          canEditOrderMovements={canEditOrderMovements}
          onCancel={resetImageModal}
          movementDetails={movementDetails}
          onUploadImage={uploadImage}
          isImageUploading={isUploading}
          hasImages={
            !!movementDetails?.delivery_attachments?.some((obj) => Object.values(obj).some((images) => images?.length))
          }
        />
      }
    >
      <Vertical className='pt-2'>
        <ContentTabs activeKey={selectedStoreNumber} items={tabItems} onChange={handleStoreChange} />
        <ImageViewer
          images={images}
          selectedImageIndex={selectedImageIndex}
          setSelectedImageIndex={setSelectedImageIndex}
          onDelete={() => deleteImage()}
          onUpload={uploadImage}
          isImageUploading={isUploading}
          canEdit={canEditOrderMovements}
        />
      </Vertical>
    </Modal>
  )
}
